import React, { useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom/cjs/react-router-dom.min';
import {
  Badge,
  Card,
  CardBody,
  Col,
  CustomInput,
  Row,
  UncontrolledTooltip,
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from 'reactstrap';
import IconButton from '~/components/IconButton';
import SaleStatusBadge from '~/components/SaleStatusBadge';
import history from '~/services/history';
import { CANCELLED } from '~/util/deliveryStatuses';
import {
  capitalize,
  formatPrice,
  formatWhatsappPhoneNumber,
} from '~/util/format';

export default function SaleInfoCard({
  sale,
  isSelected,
  onSelect,
  onDelete,
  showInfoSeller,
  params,
}) {
  const location = useLocation();
  const finalValue = useMemo(() => {
    return (
      Number(sale.amount) +
      Number(sale.freight_user) -
      (sale?.giftback?.amount && sale.giftback.amount > 0
        ? Number(sale.giftback.amount)
        : 0)
    );
  }, [sale]);

  const exceptionMoneyAndCart = useMemo(() => {
    if (
      (sale.payment_type === 201 ||
        sale.payment_type === 202 ||
        sale.payment_type === 203 ||
        sale.payment_type === 204 ||
        sale.payment_type === 401) &&
      sale?.status <= 2
    ) {
      return true;
    }
    return false;
  }, [sale]);

  const isEditable = useMemo(() => {
    if (sale?.status < 2 || exceptionMoneyAndCart) {
      return true;
    }
    return false;
  }, [sale, exceptionMoneyAndCart]);

  const isApiSale = Boolean(sale.origin_partner_application_id);
  const partnerApp =
    sale?.origin_partner_application || sale?.originPartnerApplication;
  const apiSourceName = partnerApp?.name || null;
  const apiTooltip = apiSourceName
    ? `Venda criada via API por ${apiSourceName}`
    : 'Venda criada via API';

  const [show, setShow] = useState(false);

  return (
    <Card
      style={{
        boxShadow: '0rem 0.275rem 0.3rem 0.1rem rgba(0, 0, 0, 0.035)',
        ...(sale.is_cross && {
          border: '1px solid #E8308A',
          borderLeft: '8px solid #E8308A',
        }),
        ...(isApiSale &&
          !sale.is_cross && {
            border: '1px solid #3b82f6',
            borderLeft: '8px solid #3b82f6',
          }),
      }}
      className="card new-layout list-card sale grow-on-hover overflow-hidden"
      onClick={() => {
        history.push({
          pathname: `/venda/${sale.id}`,
          state: { params },
        });
      }}
    >
      <CardBody className="card-body new-layout px-3 py-3">
        {/*  desktop version  */}

        <Row className="w-100 h-100 mx-0 d-none d-lg-flex align-items-center">
          <Col xs={1} align="center" className="h-100 d-none">
            <div
              className="h-100 d-flex flex-row justify-content-center align-items-center flex-wrap d-none"
              onClick={e => {
                e.stopPropagation();
              }}
              role="button"
              tabIndex={0}
              aria-label="checkbox-wrapper-div"
            >
              <CustomInput
                type="checkbox"
                id={`${sale.id}-checkbox`}
                className="text-primary"
                checked={isSelected}
                defaultValue={isSelected}
                onChange={() => onSelect(sale, isSelected)}
              />
            </div>
          </Col>
          <Col
            xs={showInfoSeller ? 1 : 2}
            className="d-flex flex-column align-items-start justify-content-center"
          >
            <div
              className="sale-order d-flex flex-column align-items-start w-100"
              style={{ minWidth: 0 }}
            >
              <span className="sale-order-number w-100">
                #{sale.order_number}
              </span>

              <div className="sale-badges mt-1">
                {['CORREIOS', 'CORREIOS_EXPRESS'].includes(
                  sale.delivery_method
                ) && (
                  <>
                    <Badge
                      className="correios-badge"
                      id={`correios-badge-${sale.id}`}
                    >
                      C
                    </Badge>
                    <UncontrolledTooltip
                      target={`correios-badge-${sale.id}`}
                      trigger="hover focus"
                      placement="top"
                    >
                      Essa venda utiliza Correios
                    </UncontrolledTooltip>
                  </>
                )}

                {sale.has_whatsapp_flow && (
                  <>
                    <Badge
                      className="whatsapp-flow-badge"
                      id={`whatsapp-badge-${sale.id}`}
                    >
                      <i
                        className="fab fa-whatsapp"
                        style={{ fontSize: '0.7rem' }}
                      />
                    </Badge>
                    <UncontrolledTooltip
                      target={`whatsapp-badge-${sale.id}`}
                      trigger="hover focus"
                      placement="top"
                    >
                      Essa venda utiliza fluxo WhatsApp
                    </UncontrolledTooltip>
                  </>
                )}

                {isApiSale && (
                  <>
                    <Badge
                      id={`api-badge-${sale.id}`}
                      className="api-sale-badge"
                    >
                      <i
                        className="las la-plug"
                        style={{ fontSize: '0.75rem' }}
                      />
                    </Badge>
                    <UncontrolledTooltip
                      target={`api-badge-${sale.id}`}
                      trigger="hover focus"
                      placement="top"
                    >
                      {apiTooltip}
                    </UncontrolledTooltip>
                  </>
                )}
              </div>
            </div>
          </Col>
          {showInfoSeller && (
            <Col xs={2} align="start">
              <div className="new-layout-table-column-value h-100 d-flex flex-row flex-wrap">
                {sale?.handled?.broken_name ? (
                  <>
                    {sale.handled.broken_name.map((word, index) => (
                      <div
                        key={`${word}-${sale.id}`}
                        className={`new-layout-card-list-strong-value w-100 ${
                          index > 0 && 'text-truncate'
                        }`}
                      >
                        {word}
                      </div>
                    ))}
                  </>
                ) : (
                  <div className="new-layout-card-list-strong-value w-100">
                    -
                  </div>
                )}
              </div>
            </Col>
          )}
          <Col
            xs={1}
            align="start"
            className="d-flex flex-row align-items-center flex-wrap"
          >
            {sale.client.broken_name ? (
              <>
                {sale.client.broken_name.map((word, index) => (
                  <div
                    key={`${word}-${sale.id}`}
                    className={`new-layout-card-list-strong-value w-100 ${
                      index > 0 && 'text-truncate'
                    }`}
                  >
                    {word}
                  </div>
                ))}
              </>
            ) : (
              <div className="new-layout-card-list-strong-value w-100">-</div>
            )}
          </Col>
          <Col
            xs={2}
            align="start"
            className="d-flex flex-row align-items-center flex-wrap"
          >
            {sale.store.broken_fantasy_name.map((word, index) => (
              <div
                key={`${word}-${sale.id}`}
                className={`new-layout-table-column-value w-100 ${
                  index > 0 && 'text-truncate'
                }`}
              >
                {word}
              </div>
            ))}
          </Col>
          <Col xs={1} align="start">
            <div className="new-layout-table-column-value h-100 d-flex flex-row flex-wrap">
              {finalValue ? (
                formatPrice(finalValue)
              ) : (
                <span className="ml-3 pl-3">-</span>
              )}
            </div>
          </Col>
          <Col xs={2} align="start" className="h-100 d-flex flex-row flex-wrap">
            <div className="new-layout-table-column-value h-100 d-flex flex-row flex-wrap">
              {sale.formatted_updated_at}
            </div>
          </Col>
          <Col
            xs={showInfoSeller ? 2 : 3}
            align="center"
            className="px-0 h-100 d-flex flex-row flex-wrap"
          >
            <div className="d-flex flex-row justify-content-center align-items-center h-100 w-100">
              <SaleStatusBadge cart={sale} antifraudDetail />
            </div>
          </Col>
          <Col xs={1} align="center" className="h-100 px-2">
            <div
              className={`h-100 flex-row align-items-center justify-content-around ${
                location.pathname.includes('busca') ? 'd-none' : 'd-flex'
              }`}
            >
              <a
                href={`https://wa.me/${formatWhatsappPhoneNumber(
                  sale?.client?.telephone
                )}`}
                target="_blank"
                rel="noreferrer"
                onClick={e => e.stopPropagation()}
              >
                <IconButton
                  icon="fab fa-whatsapp"
                  color="success"
                  id={`whatsapp-mobile-btn-${sale.id}`}
                  tip="Vender para Cliente"
                />
              </a>
              {/*
              {isEditable && (
                <div>
                  <IconButton
                    icon="las la-pen"
                    color="primary"
                    id={`edit-btn-${sale.id}`}
                    tip="Editar"
                    forceTip
                    click={e => {
                      e.stopPropagation();
                      // history.push(`/registrar/${sale.id}`);
                      setShow(true);
                    }}
                  />
                </div>
              )}
              */}
              {sale.status_text === 'Aberto' && (
                <div>
                  <IconButton
                    icon="las la-trash"
                    color="danger"
                    id={`trash-btn-${sale.id}`}
                    tip="Excluir"
                    forceTip
                    click={e => {
                      onDelete(sale);
                      e.stopPropagation();
                    }}
                  />
                </div>
              )}
            </div>
          </Col>
          <Modal isOpen={show}>
            <ModalHeader
              toggle={() => {
                setShow(false);
              }}
            >
              Editar venda
            </ModalHeader>
            <ModalBody>
              <h5>
                No momento, estamos atualizando nosso fluxo de edição. Para
                modificar informações, basta gerar um novo pedido com os dados
                atualizados. Se precisar de ajuda, nosso suporte está à
                disposição!
              </h5>
            </ModalBody>
            <ModalFooter>
              <Button
                className="new-layout-btn main"
                color="primary"
                onClick={e => {
                  e.stopPropagation();
                  setShow(false);
                }}
              >
                Ok
              </Button>
            </ModalFooter>
          </Modal>
        </Row>

        {/*  mobile version  */}

        <Row className="w-100 h-100 mx-0 d-lg-none">
          <Col className="px-0">
            <Row className="w-100 mx-0">
              <Col xs={10} align="start" className="px-0">
                <div className="new-layout-card-mobile font-weight-600">
                  {sale.client.name}
                </div>
              </Col>
              <Col xs={2} className="px-0" align="end">
                <div
                  className={`h-100 ${
                    location.pathname.includes('busca') ? 'd-none' : 'd-flex'
                  } flex-row ${
                    sale.status_text === 'Aberto'
                      ? 'justify-content-between'
                      : 'justify-content-end'
                  }`}
                >
                  {!isEditable && (
                    <a
                      href={`https://wa.me/${formatWhatsappPhoneNumber(
                        sale?.client?.telephone
                      )}`}
                      target="_blank"
                      rel="noreferrer"
                      onClick={e => e.stopPropagation()}
                    >
                      <IconButton
                        icon="fab fa-whatsapp"
                        color="success"
                        id={`whatsapp-mobile-btn-${sale.id}`}
                        tip="Vender para Cliente"
                      />
                    </a>
                  )}
                  {/*
                  {isEditable && (
                    <div className={`${sale.status_text !== 'Aberto' && ''}`}>
                      <IconButton
                        icon="las la-pen"
                        color="primary"
                        id={`edit-mobile-btn-${sale.id}`}
                        tip="Editar"
                        click={e => {
                          e.stopPropagation();
                          // history.push(`/registrar/${sale.id}`);
                          setShow(true);
                        }}
                      />
                    </div>
                  )}
                  */}
                  {sale.status_text === 'Aberto' && (
                    <IconButton
                      icon="las la-trash"
                      color="danger"
                      id={`trash-mobile-btn-${sale.id}`}
                      tip="Excluir"
                      click={e => {
                        onDelete(sale);
                        e.stopPropagation();
                      }}
                    />
                  )}
                </div>
              </Col>
            </Row>
            <hr />
            <Row className="w-100 mx-0 mb-2">
              <SaleStatusBadge cart={sale} />
              {sale.is_imported && (
                <Badge className="omnichat-badge ml-2">OC</Badge>
              )}
              {['CORREIOS', 'CORREIOS_EXPRESS'].includes(
                sale.delivery_method
              ) && <Badge className="correios-badge ml-2">C</Badge>}
              {sale.has_whatsapp_flow && (
                <Badge className="whatsapp-flow-badge ml-2">
                  <i
                    className="fab fa-whatsapp"
                    style={{ fontSize: '0.65rem' }}
                  />
                </Badge>
              )}
              {isApiSale && (
                <>
                  <Badge
                    id={`api-badge-mobile-${sale.id}`}
                    className="api-sale-badge ml-2"
                  >
                    <i
                      className="las la-plug"
                      style={{ fontSize: '0.75rem' }}
                    />
                  </Badge>
                  <UncontrolledTooltip
                    target={`api-badge-mobile-${sale.id}`}
                    trigger="hover focus"
                    placement="top"
                  >
                    {apiTooltip}
                  </UncontrolledTooltip>
                </>
              )}
              {/* {sale.is_shareable && (
                <Badge className="shareable-cart-badge ml-2">CC</Badge>
              )}
              {sale.delivery_scheduled_to && (
                <Badge className="scheduled-delivery-badge ml-2">CC</Badge>
              )} */}
            </Row>
            <Row className="w-100 mx-0">
              <Col className="px-0">
                <div className="d-flex flex-row align-items-baseline">
                  <div className="new-layout-card-mobile label font-weight-500 d-inline">
                    Número do Pedido:
                  </div>
                  <div className="new-layout-card-mobile value d-inline text-truncate">
                    #{sale.order_number}
                  </div>
                </div>
              </Col>
            </Row>
            {/* showInfoSeller && (
              <Row className="w-100 mx-0">
                <Col className="px-0">
                  <div className="d-flex flex-row align-items-baseline">
                    <div className="new-layout-card-mobile label d-inline">
                      Vendedor:
                    </div>
                    <div className="new-layout-card-mobile value d-inline text-truncate">
                      {sale?.seller?.name || 'Não informado'}
                    </div>
                  </div>
                </Col>
              </Row>
            ) */}
            <Row className="w-100 mx-0">
              <Col className="px-0">
                <div className="d-flex flex-row align-items-baseline">
                  <div className="new-layout-card-mobile label font-weight-500 d-inline">
                    Valor:
                  </div>
                  <div className="new-layout-card-mobile value d-inline text-truncate">
                    {finalValue ? formatPrice(finalValue) : <span>-</span>}
                  </div>
                </div>
              </Col>
            </Row>
            <Row className="w-100 mx-0">
              <Col className="px-0">
                <div className="d-flex flex-row align-items-baseline">
                  <div className="new-layout-card-mobile label font-weight-500 d-inline">
                    Loja:
                  </div>
                  <div className="new-layout-card-mobile value d-inline text-truncate">
                    {sale.store.fantasy_name}
                  </div>
                </div>
              </Col>
            </Row>
            <Row className="w-100 mx-0">
              <Col className="px-0">
                <div className="d-flex flex-row align-items-baseline">
                  <div className="new-layout-card-mobile label font-weight-500 d-inline">
                    Últ. Atualização:
                  </div>
                  <div className="new-layout-card-mobile value d-inline text-truncate">
                    {sale.formatted_updated_at}
                  </div>
                </div>
              </Col>
            </Row>

            {/* <Row className="w-100 mx-0">
              <Col className="px-0">
                <div className="d-flex flex-row align-items-baseline">
                  <div className="new-layout-card-mobile label d-inline">
                    Última Atualização:
                  </div>
                  <div className="new-layout-card-mobile value d-inline">
                    {sale.formatted_updated_at}
                  </div>
                </div>
              </Col>
            </Row> */}
          </Col>
        </Row>
      </CardBody>
      {sale?.is_cross && sale?.cross_accepted ? (
        <Row
          className="w-100 mx-0 px-3 py-2 d-lg-none"
          style={{
            backgroundColor: '#E8308A',
          }}
        >
          <Col className="px-0">
            <div className="d-flex flex-row align-items-baseline">
              <div className="new-layout-card-mobile label font-weight-500 d-inline text-white">
                Pedido recebido:
              </div>
              <div className="new-layout-card-mobile value d-inline text-white">
                {sale?.handled_by?.name
                  ? capitalize(sale?.handled_by?.name)
                  : null}
              </div>
            </div>
          </Col>
        </Row>
      ) : null}

      {(sale?.status_text === 'Malinha entregue' ||
        sale?.status_text === 'Entregue') &&
      sale?.return_delivery &&
      sale.return_delivery?.status !== CANCELLED &&
      sale?.return_delivery?.worker_name ? (
        <Row
          className="w-100 mx-0 px-3 py-2 d-lg-none"
          style={{
            backgroundColor:
              sale?.return_delivery?.status !== 'CANCELED'
                ? '#967ada'
                : '#eca120',
          }}
        >
          <Col className="px-0">
            <div className="d-flex flex-row align-items-baseline">
              <div className="new-layout-card-mobile label font-weight-500 d-inline text-white">
                Entregador:
              </div>
              <div className="new-layout-card-mobile value d-inline text-white">
                {sale.return_delivery.worker_name
                  ? capitalize(sale.return_delivery.worker_name)
                  : null}
              </div>
            </div>
          </Col>
        </Row>
      ) : null}
      {sale.status_text === 'Encaminhado por estoquista' &&
        sale.status_text !== 'Malinha entregue' &&
        !sale.return_delivery &&
        sale.delivery?.status !== CANCELLED &&
        sale?.delivery?.worker_name && (
          <Row
            className="w-100 mx-0 px-3 py-2 d-lg-none"
            style={{
              backgroundColor:
                sale?.delivery?.status !== 'CANCELED' ? '#967ada' : '#eca120',
            }}
          >
            <Col className="px-0">
              <div className="d-flex flex-row align-items-baseline">
                <div className="new-layout-card-mobile label font-weight-500 d-inline text-white">
                  Entregador:
                </div>
                <div className="new-layout-card-mobile value d-inline text-white">
                  {sale.delivery.worker_name
                    ? capitalize(sale.delivery.worker_name)
                    : null}
                </div>
              </div>
            </Col>
          </Row>
        )}
    </Card>
  );
}
